export function mapVdoCipherOtpResponse(response) {
  const payload = response?.data ?? response ?? {};

  const otp = String(payload.otp || '').trim();
  const playbackInfo = String(
    payload.playbackInfo || payload.playback_info || ''
  ).trim();

  return {
    otp,
    playbackInfo,
    videoId: payload.videoId || payload.video_id || payload.vdocipher_video_id || '',
  };
}

export function mapOfflineCourses(response) {
  const items = response?.data?.courses || response?.data || response?.courses || [];

  if (!Array.isArray(items)) return [];

  return items.map((course) => ({
    id: course.id || course.course_id,
    title: course.title || course.course_title || 'Course',
    thumbnail: course.thumbnail_url || course.thumbnail || '',
    downloadedLessons: course.downloaded_lessons_count ?? course.lessons_count ?? 0,
    totalSize: course.total_size || course.size_label || null,
  }));
}

export function mapOfflineVideos(response) {
  const chapters = response?.data?.chapters || response?.data || [];

  if (!Array.isArray(chapters)) return [];

  return chapters.map((chapter) => ({
    id: chapter.id || chapter.chapter_id,
    title: chapter.title || chapter.chapter_title || 'Chapter',
    lessons: (chapter.lessons || []).map((lesson) => ({
      id: lesson.id || lesson.lesson_id,
      title: lesson.title || lesson.lesson_title || 'Lesson',
      duration: lesson.duration_minutes ?? null,
      videoId: lesson.vdocipher_video_id || lesson.video_id || null,
      downloadedAt: lesson.downloaded_at || null,
    })),
  }));
}

export function mapVdoCipherUserAnalytics(response) {
  const data = response?.data ?? response ?? {};

  return {
    totalWatchTimeSeconds: data.total_watch_time_seconds ?? data.total_watch_time ?? 0,
    totalVideosWatched: data.total_videos_watched ?? data.videos_watched ?? 0,
    averageCompletionRate: data.average_completion_rate ?? data.completion_rate ?? null,
    recentActivity: Array.isArray(data.recent_activity) ? data.recent_activity : [],
    raw: data,
  };
}

export function isVdoCipherLesson(lesson) {
  if (!lesson) return false;
  const source = String(lesson.videoSource || lesson.video_source || '').toLowerCase();
  if (source === 'vdocipher') return true;
  return Boolean(lesson.vdocipherVideoId || lesson.vdocipher_video_id);
}

export function isYouTubeUrl(url) {
  return /youtube\.com|youtu\.be/i.test(String(url || ''));
}

export function isYouTubeLesson(lesson) {
  if (!lesson) return false;
  const source = String(lesson.videoSource || lesson.video_source || '').toLowerCase();
  if (source === 'youtube') return true;
  const url = lesson.youtubeUrl || lesson.youtube_url;
  return isYouTubeUrl(url);
}

export function buildLessonVideoTarget(lesson) {
  if (!lesson) return null;

  return {
    otpType: 'lesson',
    id: lesson.id,
    title: lesson.title,
    videoSource: lesson.videoSource || lesson.video_source,
    youtubeUrl: lesson.youtubeUrl || lesson.youtube_url,
    vdocipherVideoId: lesson.vdocipherVideoId || lesson.vdocipher_video_id,
    isFreePreview: Boolean(lesson.isFreePreview ?? lesson.is_free_preview),
    watchedSeconds: Number(lesson.watchedSeconds ?? lesson.watched_seconds) || 0,
  };
}

export function buildCourseVideoTarget(courseId, meta = {}) {
  if (!courseId) return null;

  return {
    otpType: 'course',
    courseId,
    title: meta.title,
    videoSource: meta.videoSource || meta.video_source,
    youtubeUrl: meta.youtubeUrl || meta.youtube_url || meta.previewVideoUrl,
    vdocipherVideoId: meta.vdocipherVideoId || meta.vdocipher_video_id,
    isFreePreview: true,
  };
}

export function buildUrlVideoTarget({ title, videoUrl, youtubeUrl } = {}) {
  const directUrl = videoUrl && !isYouTubeUrl(videoUrl) ? videoUrl : null;
  const ytUrl = youtubeUrl || (videoUrl && isYouTubeUrl(videoUrl) ? videoUrl : null);

  if (!directUrl && !ytUrl) return null;

  return {
    otpType: 'url',
    title,
    youtubeUrl: ytUrl,
    videoUrl: directUrl,
    isFreePreview: true,
  };
}

export function hasPlayableVideoTarget(target) {
  if (!target) return false;
  return (
    isVdoCipherLesson(target) ||
    isYouTubeLesson(target) ||
    Boolean(target.videoUrl) ||
    isYouTubeUrl(target.youtubeUrl)
  );
}

export function toYouTubeEmbedUrl(url) {
  const value = String(url || '');
  const match =
    value.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/) ||
    value.match(/[?&]v=([A-Za-z0-9_-]{6,})/) ||
    value.match(/\/embed\/([A-Za-z0-9_-]{6,})/);

  const id = match?.[1];
  return id ? `https://www.youtube.com/embed/${id}?rel=0&enablejsapi=1` : null;
}
